import React, { useState } from 'react';

/* ── Single completed task row (struck through, dimmed) ── */
function CompletedTaskRow({ item, formatDate, onItemClick, onToggle, completingId, indent }) {
  return (
    <div
      className={`completed-row ${indent ? 'completed-row--indent' : ''}`}
      onClick={() => onItemClick(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemClick(item); } }}
    >
      {/* Dimmed green checkmark */}
      <div className="completed-row-check">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="var(--status-success, #22c55e)" opacity="0.5" />
          <path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="completed-row-body">
        <span className="completed-row-title">{item.title}</span>
        <span className="completed-row-meta">
          {formatDate(item.date)}
          {item.endeavorName && <><span className="completed-row-sep"> · </span>{item.endeavorName}</>}
        </span>
      </div>

      {/* Undo (un-complete) button */}
      {item.type === 'assignment' && (
        <button
          className="completed-row-undo"
          onClick={(e) => { e.stopPropagation(); onToggle(item); }}
          disabled={completingId === item.id}
          aria-label="Mark incomplete"
          title="Mark Incomplete"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
        </button>
      )}
    </div>
  );
}

/* ── Completed session accordion (event header + all-done child tasks) ── */
function CompletedSessionAccordion({ group, formatDate, onItemClick, onEventClick, onToggle, completingId }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="completed-session">
      <div
        className="completed-session-header"
        onClick={() => setExpanded(prev => !prev)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(prev => !prev); } }}
      >
        {/* Calendar icon */}
        <svg className="completed-session-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>

        <span
          className="completed-session-title"
          onClick={(e) => { e.stopPropagation(); if (group.raw && onEventClick) onEventClick(group); }}
          role="link"
          tabIndex={0}
          title="View event details"
        >
          {group.title}
        </span>

        <span className="completed-session-meta">{formatDate(group.date)}</span>

        <span className="completed-session-count">
          {group.doneTasks} task{group.doneTasks !== 1 ? 's' : ''}
        </span>

        <svg
          className={`completed-session-chevron ${expanded ? 'completed-session-chevron--open' : ''}`}
          width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </div>

      {expanded && (
        <div className="completed-session-children">
          {group.assignments.map(task => (
            <CompletedTaskRow
              key={`${task.type}-${task.id}`}
              item={task}
              formatDate={formatDate}
              onItemClick={onItemClick}
              onToggle={onToggle}
              completingId={completingId}
              indent
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Completed Section ── */
function CompletedSection({ completedGroups, formatDate, onItemClick, onEventClick, onToggle, completingId, showCompleted, setShowCompleted }) {
  const totalTasks = completedGroups.reduce((n, g) => n + g.totalTasks, 0);

  // Last completed task for collapsed preview
  const lastCompleted = React.useMemo(() => {
    for (const g of completedGroups) {
      const tasks = [...g.assignments].sort(
        (a, b) => (b.completedAt || '').localeCompare(a.completedAt || '')
      );
      if (tasks.length > 0) return tasks[0];
    }
    return null;
  }, [completedGroups]);

  return (
    <div className="completed-section">
      {/* Collapsible section header */}
      <div
        className="completed-section-header"
        onClick={() => setShowCompleted(prev => !prev)}
        role="button"
        tabIndex={0}
        aria-expanded={showCompleted}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowCompleted(prev => !prev); } }}
      >
        <div className="completed-section-left">
          <svg
            className={`completed-section-chevron ${showCompleted ? 'completed-section-chevron--open' : ''}`}
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="6,9 12,15 18,9" />
          </svg>
          <span className="completed-section-label">Completed</span>
          {!showCompleted && lastCompleted && (
            <span className="completed-section-preview">
              Last: {lastCompleted.title}
            </span>
          )}
          <span className="completed-section-count">{totalTasks}</span>
        </div>
      </div>

      {/* Session accordions — sorted by completedAt DESC upstream */}
      {showCompleted && completedGroups.length > 0 && (
        <div className="completed-list-wrapper">
          {completedGroups.map(group => (
            <CompletedSessionAccordion
              key={group.id}
              group={group}
              formatDate={formatDate}
              onItemClick={onItemClick}
              onEventClick={onEventClick}
              onToggle={onToggle}
              completingId={completingId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default React.memo(CompletedSection);
